/**
 * TradeLogic design tokens — single source of truth.
 *
 * Frozen at module load. Consumers must NEVER mutate; all four frontends
 * (admin / vendor / portal / mobile) import from here and adapt to their
 * own styling layer (CSS-in-JS, RN StyleSheet, etc.).
 */
export const tokens = Object.freeze({
    color: Object.freeze({
        brand: Object.freeze({
            50: '#eef4ff',
            100: '#d9e6ff',
            200: '#b5cdff',
            300: '#8aafff',
            400: '#5d8dff',
            500: '#3b6dff',
            600: '#264fe0',
            700: '#1d3eb3',
            800: '#172f87',
            900: '#0f1f5c',
        }),
        neutral: Object.freeze({
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
        }),
        semantic: Object.freeze({
            background: '#ffffff',
            surface: '#f8fafc',
            border: '#e2e8f0',
            textPrimary: '#0f172a',
            textSecondary: '#475569',
            textInverse: '#ffffff',
            success: '#16a34a',
            warning: '#d97706',
            danger: '#dc2626',
            info: '#0284c7',
            focusRing: '#3b6dff',
        }),
    }),
    spacing: Object.freeze({
        xxs: 2,
        xs: 4,
        sm: 8,
        md: 12,
        lg: 16,
        xl: 24,
        xxl: 32,
        xxxl: 48,
    }),
    typography: Object.freeze({
        fontFamily: Object.freeze({
            sans: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            mono: '"JetBrains Mono", Menlo, Consolas, monospace',
        }),
        fontSize: Object.freeze({
            xs: 11,
            sm: 13,
            md: 15,
            lg: 18,
            xl: 22,
            xxl: 28,
            display: 36,
        }),
        fontWeight: Object.freeze({
            regular: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
        }),
        lineHeight: Object.freeze({
            tight: 1.2,
            normal: 1.4,
            relaxed: 1.6,
        }),
        letterSpacing: Object.freeze({
            tight: '-0.01em',
            normal: '0',
            wide: '0.02em',
        }),
    }),
    radii: Object.freeze({
        none: 0,
        sm: 4,
        md: 8,
        lg: 12,
        pill: 999,
        circle: 9999,
    }),
    shadows: Object.freeze({
        none: 'none',
        sm: '0 1px 2px rgba(15, 23, 42, 0.06)',
        md: '0 4px 12px rgba(15, 23, 42, 0.10)',
        lg: '0 12px 28px rgba(15, 23, 42, 0.16)',
        overlay: '0 24px 64px rgba(15, 23, 42, 0.24)',
    }),
    motion: Object.freeze({
        duration: Object.freeze({
            instant: 0,
            fast: 120,
            normal: 200,
            slow: 320,
        }),
        easing: Object.freeze({
            standard: 'cubic-bezier(0.2, 0, 0, 1)',
            accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
            decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
        }),
    }),
    zIndex: Object.freeze({
        base: 0,
        dropdown: 1000,
        sticky: 1100,
        modal: 1300,
        toast: 1400,
        tooltip: 1500,
    }),
    borderWidth: Object.freeze({
        none: 0,
        thin: 1,
        medium: 2,
        thick: 3,
        heavy: 4,
    }),
    breakpoints: Object.freeze({
        mobile: 320,
        tablet: 768,
        laptop: 1024,
        desktop: 1440,
        wide: 1920,
    }),
});
//# sourceMappingURL=tokens.js.map